import { Link, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  Mic,
  BarChart3,
  Users,
  UsersRound,
  ClipboardCheck,
  Settings,
  LogOut,
  ChevronLeft,
  Menu,
  UserCog,
  GraduationCap,
  BookOpen,
} from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import OracyLogo from "@/components/OracyLogo";
import { useLogout, useCurrentUser } from "@/lib/api";

interface NavItem {
  label: string;
  href: string;
  icon: React.ReactNode;
}

const studentNav: NavItem[] = [
  { label: "Dashboard", href: "/student", icon: <LayoutDashboard className="w-5 h-5" /> },
  { label: "My Tasks", href: "/student/tasks", icon: <Mic className="w-5 h-5" /> },
  { label: "Progress", href: "/student/progress", icon: <BarChart3 className="w-5 h-5" /> },
];

const teacherNav: NavItem[] = [
  { label: "Dashboard", href: "/teacher", icon: <LayoutDashboard className="w-5 h-5" /> },
  { label: "Review", href: "/teacher/review", icon: <ClipboardCheck className="w-5 h-5" /> },
  { label: "Students", href: "/teacher/students", icon: <Users className="w-5 h-5" /> },
  { label: "Cohorts", href: "/teacher/cohorts", icon: <BookOpen className="w-5 h-5" /> },
  { label: "Progress", href: "/teacher/progress", icon: <BarChart3 className="w-5 h-5" /> },
];

const adminNav: NavItem[] = [
  { label: "Dashboard", href: "/admin", icon: <LayoutDashboard className="w-5 h-5" /> },
  { label: "Teachers", href: "/admin/teachers", icon: <UserCog className="w-5 h-5" /> },
  { label: "Students", href: "/admin/students", icon: <GraduationCap className="w-5 h-5" /> },
  { label: "Parents", href: "/admin/parents", icon: <Users className="w-5 h-5" /> },
  { label: "Cohorts", href: "/admin/cohorts", icon: <UsersRound className="w-5 h-5" /> },
  { label: "Benchmarks", href: "/admin/benchmarks", icon: <BookOpen className="w-5 h-5" /> },
];

interface AppLayoutProps {
  children: React.ReactNode;
  role: "student" | "teacher" | "admin";
}

export default function AppLayout({ children, role }: AppLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const nav = role === "student" ? studentNav : role === "teacher" ? teacherNav : adminNav;
  const { data: user } = useCurrentUser();
  const logoutMutation = useLogout();

  const handleLogout = async () => {
    await logoutMutation.mutateAsync();
    navigate('/login');
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-foreground/20 backdrop-blur-sm md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex flex-col bg-card border-r border-border transition-all duration-300",
          collapsed ? "w-16" : "w-60",
          mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        )}
      >
        {/* Logo */}
        <div className={cn(
          "flex items-center h-14 md:h-16 lg:h-[72px] px-4 border-b border-border overflow-hidden",
          collapsed ? "justify-center" : "justify-between"
        )}>
          {!collapsed && (
            <div className="flex-1 min-w-0 flex items-center">
              <OracyLogo
                variant="full"
                theme="light"
                linkTo={role === "student" ? "/student" : role === "teacher" ? "/teacher" : "/admin"}
              />
            </div>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="hidden md:flex items-center justify-center w-8 h-8 rounded-md hover:bg-secondary transition-colors text-muted-foreground shrink-0"
          >
            <ChevronLeft className={cn("w-4 h-4 transition-transform", collapsed && "rotate-180")} />
          </button>
        </div>

        {/* Role badge */}
        {!collapsed && (
          <div className="px-4 py-3">
            <span className={cn(
              "inline-flex items-center px-3 py-1 rounded-full text-xs font-medium",
              role === "student"
                ? "bg-accent text-accent-foreground"
                : "bg-primary text-primary-foreground"
            )}>
              {role === "student" ? "🎤 Student" : role === "teacher" ? "📋 Teacher" : "⚙️ Admin"}
            </span>
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 px-2 py-2 space-y-1">
          {nav.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setMobileOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
                  isActive
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                )}
              >
                {item.icon}
                {!collapsed && <span>{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="px-2 py-3 border-t border-border space-y-1">
          <button
            onClick={handleLogout}
            disabled={logoutMutation.isPending}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-secondary transition-colors"
          >
            <LogOut className="w-5 h-5" />
            {!collapsed && <span>{logoutMutation.isPending ? "Signing out..." : "Logout"}</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className={cn("flex-1 min-w-0 transition-all duration-300", collapsed ? "md:ml-16" : "md:ml-60")}>
        {/* Top bar */}
        <header className="sticky top-0 z-30 flex items-center h-14 md:h-16 lg:h-[72px] px-4 bg-card/80 backdrop-blur-sm border-b border-border md:px-6">
          <button
            onClick={() => setMobileOpen(true)}
            className="mr-3 md:hidden text-muted-foreground"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-8 h-8 rounded-full bg-primary flex items-center justify-center hover:opacity-80 transition-opacity">
                  <span className="text-xs font-medium text-primary-foreground">
                    {user?.first_name?.[0] || (role === "student" ? "S" : role === "teacher" ? "T" : "A")}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem asChild>
                  <Link to={`/${role}/settings`} className="flex items-center gap-2 cursor-pointer">
                    <Settings className="w-4 h-4" />
                    Account Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  disabled={logoutMutation.isPending}
                  className="flex items-center gap-2 cursor-pointer text-destructive focus:text-destructive"
                >
                  <LogOut className="w-4 h-4" />
                  {logoutMutation.isPending ? "Signing out..." : "Sign Out"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 md:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
